<template>
	<Header title="Entrar por email" />
	<form class="gap-y-6 flex flex-col mt-6 px-4" @submit.prevent="submitLogin" v-if="!emailSent">
		<p class="text-brand-neutral-4 text-sm text-center">
			Você receberá um link por email para acessar sua conta de forma rápida e fácil.
		</p>
		<Input v-model="email" placeholder="Informe o seu e-mail" required type="email">
			<template #label>E-mail</template>
			<template #prependIcon>
				<span class="fi fi-br-envelope text-brand-neutral-4-75 text-base flex"></span>
			</template>
		</Input>
		<Button :isLoading="loading" type="submit">Entrar</Button>
	</form>
	<form class="gap-y-6 flex flex-col mt-6 px-4" v-if="emailSent">
		<h2 class="text-xl font-bold text-brand-neutral-7 text-center">Insira o código de 6 dígitos.</h2>
		<p class="text-center text-brand-neutral-4 text-sm">
			Seu código foi enviado para <br />
			<strong>{{ email }}</strong>
		</p>
		<div class="flex flex-row gap-2">
			<Input
				ref="firstInputRef"
				v-model="token1"
				required
				type="text"
				inputmode="numeric"
				id="token-1"
				class="text-center"
				maxlength="1"
				:disabled="loading"
				@keyup="handleInputBehavior"
			>
			</Input>
			<Input
				v-model="token2"
				required
				type="text"
				inputmode="numeric"
				id="token-2"
				class="text-center"
				maxlength="1"
				:disabled="loading"
				@keyup="handleInputBehavior"
			>
			</Input>
			<Input
				v-model="token3"
				required
				type="text"
				inputmode="numeric"
				id="token-3"
				class="text-center"
				maxlength="1"
				:disabled="loading"
				@keyup="handleInputBehavior"
			>
			</Input>
			<Input
				v-model="token4"
				required
				type="text"
				inputmode="numeric"
				id="token-4"
				class="text-center"
				maxlength="1"
				:disabled="loading"
				@keyup="handleInputBehavior"
			>
			</Input>
			<Input
				v-model="token5"
				required
				type="text"
				inputmode="numeric"
				id="token-5"
				class="text-center"
				maxlength="1"
				:disabled="loading"
				@keyup="handleInputBehavior"
			>
			</Input>
			<Input
				v-model="token6"
				required
				type="text"
				inputmode="numeric"
				id="token-6"
				class="text-center"
				maxlength="1"
				:disabled="loading"
				@keyup="handleInputBehavior"
			>
			</Input>
		</div>
		<div>
			<p class="text-center text-brand-neutral-4 text-sm" v-if="resetEmailTimer">
				Aguarde <b>{{ formattedEmailTimer }}</b> para poder enviar outro código novamente.
			</p>
			<Button
				v-else
				variant="transparent"
				class="text-brand-primary-1 text-sm font-bold py-0 text-start px-0 h-auto"
				@click="submitLogin"
				:IsLoading="loading"
				>Reenviar código</Button
			>
		</div>
		<Button :isLoading="loading" type="submit">Entrar</Button>
	</form>
</template>
<script lang="ts" setup>
import dayjs, { Dayjs } from 'dayjs'
import duration from 'dayjs/plugin/duration'

dayjs.extend(duration)

const email = ref('')
const loading = ref(false)
const alertsStore = useAlertsStore()
const { $api } = useNuxtApp()
const emailSent = ref(false)
const token1 = ref('')
const token2 = ref('')
const token3 = ref('')
const token4 = ref('')
const token5 = ref('')
const token6 = ref('')
const router = useRouter()
const firstInputRef = useTemplateRef('firstInputRef')
const resetEmailTimer = ref<Dayjs | null>(null)
const formattedEmailTimer = ref('')
const lastPastedContent = ref('')
const submitLogin = async () => {
	if (loading.value) {
		return
	}

	loading.value = true

	try {
		await $api('/auth/magic-link', {
			method: 'POST',
			body: {
				email: email.value,
			},
		})

		alertsStore.addAlert({
			title: 'Verifique seu email.',
			variant: 'success',
		})

		emailSent.value = true

		resetEmailTimer.value = dayjs().add(2, 'minutes')

		setTimeout(() => {
			firstInputRef.value?.inputRef?.focus()
		}, 300)

		await addPasteListener()
	} catch (error) {
		console.error(error)

		alertsStore.addAlert({
			title: 'Erro ao enviar e-mail..',
			variant: 'danger',
		})
	}

	loading.value = false
}

const handlePaste = (string: any) => {
	if (!emailSent.value && loading.value) {
		return
	}

	const tokens = string.split('')

	if (tokens.length !== 6) {
		return
	}

	const tokenRefs = [token1, token2, token3, token4, token5, token6]
	tokens.forEach((token: string, index: number) => {
		tokenRefs[index].value = token
	})

	tokenLogin(tokens.join(''))
}

const handleInputBehavior = (event: any) => {
	const tokenRefs = [token1, token2, token3, token4, token5, token6]
	const currentId = event.target.id
	const currentIndex = parseInt(currentId.split('-')[1]) - 1

	if (event.key === 'Backspace' && currentIndex > 0) {
		const previousInput: any = document.querySelector(`input#token-${currentIndex}`)
		if (previousInput) {
			previousInput.focus()
		}
	}

	// Move to the next input if the current input is filled
	if (event.target.value.length === 1 && currentIndex < tokenRefs.length - 1) {
		const nextInput: any = document.querySelector(`input#token-${currentIndex + 2}`)
		if (nextInput) {
			nextInput.focus()
		}
	}

	// Check if all tokens are filled
	const allFilled = tokenRefs.every(ref => ref.value.length === 1)
	if (allFilled) {
		const token = tokenRefs.map(ref => ref.value).join('')
		tokenLogin(token)
	}
}

const tokenLogin = async (token: string) => {
	if (loading.value) {
		return
	}
	loading.value = true
	try {
		if (token.length < 6) {
			return
		}

		if (!/^\d+$/.test(token)) {
			alertsStore.addAlert({
				title: 'Código invalido.',
				variant: 'danger',
			})
			return
		}

		const res = await $api('/auth/login', {
			method: 'POST',
			body: {
				email: email.value,
				token: token,
			},
		})

		await router.push(
			res.user.personalInfo.document.number ? '/account' : '/account/complete-profile'
		)
	} catch (error) {
		console.error(error)

		alertsStore.addAlert({
			title: 'Erro ao logar',
			variant: 'danger',
		})
	}

	loading.value = false
}

const updateFormattedEmailTimer = () => {
	if (!resetEmailTimer.value) {
		formattedEmailTimer.value = ''
		return
	}

	const diff = resetEmailTimer.value.diff(dayjs(), 'seconds')
	const formattedDuration = dayjs.duration(diff, 'seconds').format('mm:ss')

	if (diff <= 0) {
		resetEmailTimer.value = null
	} else {
		formattedEmailTimer.value = formattedDuration
	}
}

onMounted(() => {
	const interval = setInterval(updateFormattedEmailTimer, 1000)

	onUnmounted(() => {
		clearInterval(interval)
	})
})

const addPasteListener = async () => {
	const { Capacitor } = await import('@capacitor/core')

	if (Capacitor.isNativePlatform()) {
		const interval = setInterval(async () => {
			const { Clipboard } = await import('@capacitor/clipboard')

			const { value } = await Clipboard.read()
			if (value && value.length === 6 && value !== lastPastedContent.value) {
				lastPastedContent.value = value
				handlePaste(value)
			}
		}, 10)

		onUnmounted(() => {
			clearInterval(interval)
		})
	} else {
		const onDocumentPasteHandler = (event: any) => {
			handlePaste(event.clipboardData?.getData('text') || '')
		}

		document.addEventListener('paste', onDocumentPasteHandler)

		onUnmounted(() => {
			document.removeEventListener('paste', onDocumentPasteHandler)
		})
	}
}

definePageMeta({
	middleware: 'authorize-guest',
})
</script>
